import React, { useEffect, useState } from 'react';
import {
  Device,
  EnergyConverter,
  useEnergyConverterDetailQuery,
  useEnergyConverterDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
// import { DeviceAlerts } from '../shared-ui/alerts-info';

interface EnergyConverterProps {
  id: string;
}

export const EnergyConverterComponent: React.FC<EnergyConverterProps> = ({
  id,
}) => {
  const [device, setDevice] = useState<Partial<EnergyConverter>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const { data } = useEnergyConverterDetailQuery({
    variables: { id },
  });

  useEnergyConverterDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energyConverterById) {
      setDevice(data.energyConverterById as Partial<EnergyConverter>);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation[] = [
        {
          'Device Identification': {
            'Device Name': device.name ?? 'Unnamed Energy Converter',
            'Device ID': device.deviceId ?? 'Unidentified',
            'Device Type': device.typeDisplayName ?? 'Unknown',
            'Last Updated': moment(device.timestamp).fromNow(),
          },
          'Power Metrics': {
            'Power Output': formatDeviceMetric(device.powerOutput),
            Efficiency: formatDeviceMetric(device.efficiency),
          },
          'Voltage Metrics': {
            'Input Voltage': formatDeviceMetric(device.inputVoltage),
            'Output Voltage': formatDeviceMetric(device.outputVoltage),
          },
          'Battery Management': {
            'Charge Rate': formatDeviceMetric(device.chargeRate),
            'Discharge Rate': formatDeviceMetric(device.dischargeRate),
          },
          'System Status': {
            'Power State': device.powerState?.value ? 'On' : 'Off',
            'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
            'Update Status': `${device.updateStatus ?? 'Unknown'}`,
          },
          'System Integration': {
            Property: device.property?.title ?? 'Not assigned',
            Unit: device.unit?.name ?? 'Not assigned',
            Panel: device.panel?.displayName ?? 'Not assigned',
            Source: device.source?.name ?? 'Not assigned',
          },
        },
      ];
      setDeviceInfo(info);
    }
  }, [device]);

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return (
    <PanelFrame
      title="Energy Converter"
      iconType="mdi:transmission-tower-export"
      device={device as Partial<Device>}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Power Output
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric(device.powerOutput)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Efficiency
            </Typography>
            <Typography variant="h6" align="center">
              {formatDeviceMetric(device.efficiency)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              align="center"
            >
              Status
            </Typography>
            <Typography variant="h6" align="center">
              {device.powerState?.value ? 'On' : 'Off'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PanelFrame>
  );
};
